import { useState, useEffect, useCallback } from "react";
import { Search, ExternalLink, Percent } from "lucide-react";
import { Shimmer, CardGridShimmer } from "@/components/ui/shimmer";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import { getBBPSOperators, type BBPSOperator, type BBPSFilters } from "@/utils/bbps-api";

const serviceCategories = [
  { value: "ALL", label: "All Services" },
  { value: "PREPAID", label: "Prepaid" },
  { value: "DTH", label: "DTH" },
  { value: "ELECTRICITY", label: "Electricity" },
  { value: "GAS", label: "Gas" },
  { value: "WATER", label: "Water" },
  { value: "BROADBAND", label: "Broadband" },
  { value: "LANDLINE", label: "Landline" },
  { value: "POSTPAID", label: "Postpaid" },
];

function formatDiscount(bp: number): string {
  return (bp / 100).toFixed(2) + "%";
}

function getDiscountBadgeVariant(bp: number): "default" | "secondary" | "outline" {
  if (bp >= 3000) return "default";
  if (bp >= 1000) return "secondary";
  if (bp > 0) return "outline";
  return "secondary";
}

export default function BBPSDiscountPage() {
  const { token } = useAuth();

  const [operators, setOperators] = useState<BBPSOperator[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [page, setPage] = useState(1);
  const perPage = 12;
  const [search, setSearch] = useState("");
  const [service, setService] = useState("");

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    setError("");

    const filters: BBPSFilters = {};
    if (service) filters.service = service;
    if (search.trim()) filters.search = search.trim();

    try {
      const result = await getBBPSOperators(filters, perPage, (page - 1) * perPage, token);
      setOperators(result.operators);
      setTotal(result.total);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load operators");
    } finally {
      setLoading(false);
    }
  }, [token, page, search, service]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { setPage(1); }, [search, service]);

  const totalPages = Math.ceil(total / perPage);

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-1">
            BBPS &mdash; Discount Mapping
          </h1>
          <p className="text-muted-foreground">
            View BBPS operators and their cashback/discount rates.
          </p>
        </div>
        <a href="https://docs.superflow.run/" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-lg border border-border bg-background px-2.5 py-1 text-sm font-medium hover:bg-muted transition-colors">
          API Docs <ExternalLink className="h-4 w-4" />
        </a>
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between flex-wrap gap-4 mb-6">
        <span className="text-sm text-muted-foreground font-medium">
          {total} operator{total !== 1 ? "s" : ""}
        </span>
        <div className="flex gap-3 items-center">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search operators..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-8 w-[220px]"
            />
          </div>
          <Select value={service || "ALL"} onValueChange={(v) => setService(!v || v === "ALL" ? "" : v)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="All Services" />
            </SelectTrigger>
            <SelectContent>
              {serviceCategories.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {error && (
        <Alert variant="destructive" className="mb-6">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      <Shimmer loading={loading} fallback={<CardGridShimmer />}>
        {operators.length === 0 ? (
          <div className="flex justify-center py-20">
            <p className="text-muted-foreground">
              {search || service ? "No operators match your filters" : "No operators available"}
            </p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
              {operators.map((op) => (
                <Card
                  key={op.code}
                  className="h-full transition-all duration-200 hover:border-primary hover:-translate-y-0.5 hover:shadow-lg"
                >
                  <CardContent className="p-5 flex flex-col h-full">
                    {/* Logo + Service badge */}
                    <div className="flex items-start justify-between mb-4">
                      <Avatar className="h-12 w-12 rounded-lg border">
                        <AvatarImage src={op.image} alt={op.name} />
                        <AvatarFallback className="rounded-lg">{op.name[0]}</AvatarFallback>
                      </Avatar>
                      <Badge variant="outline" className="text-[0.7rem] capitalize">
                        {op.service}
                      </Badge>
                    </div>

                    {/* Operator name */}
                    <span className="text-sm font-bold mb-1">
                      {op.name}
                    </span>

                    {/* Code */}
                    <span className="text-xs text-muted-foreground flex-1">
                      Code: {op.code}
                    </span>

                    {/* Discount footer */}
                    <div className="flex items-center justify-between mt-4 pt-3 border-t">
                      <div className="flex items-center gap-1">
                        <Percent className="h-3.5 w-3.5 text-muted-foreground" />
                        <span className="text-xs text-muted-foreground">
                          Discount
                        </span>
                      </div>
                      <Badge variant={getDiscountBadgeVariant(op.discount_percentage)}>
                        {op.discount_percentage > 0 ? formatDiscount(op.discount_percentage) : "No discount"}
                      </Badge>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex justify-center items-center gap-2 mt-6">
                <Button variant="outline" size="sm" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <span className="text-sm text-muted-foreground">Page {page} of {totalPages}</span>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            )}
          </>
        )}
      </Shimmer>
    </div>
  );
}
